// useFiles — the files library's data hooks (ADR-068): ONE question asked five
// ways over an anchor (the shelf whole · the flow one page at a time, grouped
// by the server's dividers · the collections · the bin · the options), the surface's read, the batched signed links (one
// mint per visible chunk, cached just under the TTL), the upload as announce →
// do → confirm from the browser's side (one JSON POST, one raw PUT of the bytes,
// then the producer-made sizes for an image), and the verbs. Anchor-generic —
// every container passes `<kind>:<id>`; the site's tab is the first.
//
// The shelf, the flow, the collections, the bin and the surface's read are LIVE
// reads (`liveGet` — FRONTEND-PATTERNS §Live updates): while on screen they
// follow what arrives from the field — the flow's HEAD only (ADR-075 §3): its
// first page, as the hub's; a deeper page is read when asked and refreshed by
// the person's own act. The options and the signed links are not live.

import type { CountResponse } from '@ems-app/schemas/common/pagination';
import { useMemo, useRef, useState } from 'react';
import { queryOptions, useMutation, useQueries, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type {
  AnnouncedFile,
  BinResponse,
  CollectionsResponse,
  FileDetail,
  FileNote,
  FilesOptionsResponse,
  LibraryFile,
  MediaResponse,
  PreviewLinksResponse,
  PreviewLinkResponse,
  ShelfResponse,
} from '@ems-app/schemas/files/responses';
import type { AnnounceFileRequest, ClassifyFileRequest, ClassifyFilesRequest, MoveFileRequest, UpdateFileRequest } from '@ems-app/schemas/files/requests';
import { FLOW_PAGE_SIZE, MEDIA_TYPE_FILTERS, PREVIEW_LINK_BATCH_MAX, PREVIEW_LINK_TTL_SECONDS, type ContentFamily, type DerivativeKind, type FileTypeFilter, type MediaGrain } from '@ems-app/schemas/files/vocabulary';
import { apiClient } from '../lib/apiClient.js';
import { liveGet } from '../lib/live/liveGet.js';
import { framed, type ListFrame } from '../lib/bands.js';
import { catalogShaOf, isCatalogFileId } from '../lib/catalogFileId.js';
import { downloadBlob } from '../lib/downloadBlob.js';
import { makeDerivatives } from '../lib/imageDerivatives.js';
import type { StagePreview } from '../components/patterns/filesDisplay.js';
import { refreshOnSettled } from '../lib/settled.js';

const OCTET = { 'Content-Type': 'application/octet-stream' };
/** Byte transfers ride no wall-clock timeout (the backend's idle watchdog is the guard). */
const NO_TIMEOUT = { timeout: 0 };
/** A POST that changes nothing a screen shows — a signed-link mint, an upload's
 *  announce (the server declares these routes `moves: none`): never "the
 *  person's own write", so it asks the live client for nothing (lib/apiClient). */
const MINT = { movesNothing: true };

export type PreviewKind = 'original' | DerivativeKind;

/** The question's filters as the wire takes them (`csv` lists). */
export interface FilesQuestion {
  anchor: string;
  q?: string;
  folder?: string;
  topic?: string;
  asset?: string;
  position?: string;
  person?: string;
  /** The Created period (a word or a day pair — ADR-082 §5) with the screen's zone it is cut in — the zone also
   *  cuts the flow's dividers (ADR-078 §4): it rides whenever a period or a grain does. */
  created?: string;
  tz?: string;
  type?: string;
}

export const filesKeys = {
  all: (projectId: string) => ['files', projectId] as const,
  options: (projectId: string, anchor: string) => ['files', projectId, 'options', anchor] as const,
  shelf: (projectId: string, q: FilesQuestion) => ['files', projectId, 'shelf', q] as const,
  media: (projectId: string, q: FilesQuestion, review: boolean, sort: string, group: string, tz: string, page: number, limit: number) => ['files', projectId, 'media', q, review, sort, group, tz, page, limit] as const,
  collections: (projectId: string, q: FilesQuestion, by: string, review: boolean, sort: string) => ['files', projectId, 'collections', q, by, review, sort] as const,
  bin: (projectId: string, anchor: string) => ['files', projectId, 'bin', anchor] as const,
  detail: (projectId: string, fileId: string) => ['files', projectId, 'file', fileId] as const,
  /** Their own roots — a mutation's prefix invalidation of `all` must never re-mint
   *  a signed link (each mint of a sensitive class is an audit row). `preview` is
   *  the stage's per-open link; `previews` a visible chunk of tile thumbs. */
  preview: (projectId: string, fileId: string) => ['file-preview', projectId, fileId] as const,
  previews: (projectId: string, chunk: string) => ['file-previews', projectId, chunk] as const,
};

const clean = (q: FilesQuestion): Record<string, string> => Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== '')) as Record<string, string>;

// ── the reads ───────────────────────────────────────────────────────────────

export function useFilesOptions(projectId: string, anchor: string): UseQueryResult<FilesOptionsResponse, Error> {
  return useQuery({
    queryKey: filesKeys.options(projectId, anchor),
    queryFn: () => apiClient.get(`/projects/${projectId}/files/options`, { params: { anchor } }).then((r) => r.data as FilesOptionsResponse),
    enabled: projectId.length > 0 && anchor.length > 0,
    staleTime: 60_000,
  });
}

export function useShelf(projectId: string, q: FilesQuestion): UseQueryResult<ShelfResponse, Error> {
  return useQuery({
    queryKey: filesKeys.shelf(projectId, q),
    queryFn: (ctx) => liveGet<ShelfResponse>(ctx, `/projects/${projectId}/files/shelf`, { params: clean(q) }),
    enabled: projectId.length > 0,
    placeholderData: (prev) => prev,
  });
}

/** The flow's view: the lens, the sort, the dividers' grain (null = flat) with the screen's zone it cuts in, and the
 *  page (1-based). The zone rides HERE with a grain — not in the shared question — so the shelf never re-reads for a
 *  grouping toggle (its key holds the question whole). */
export interface MediaView {
  review: boolean;
  sort: 'newest' | 'oldest';
  group: MediaGrain | null;
  /** The zone the dividers are cut in; required by the server with a grain. */
  tz?: string;
  page: number;
  /** The rows asked — the flow's page by default; a glance that shows a handful (the ticket peek's thumbs) asks for
   *  that handful alone, so its live look-again re-reads six labelled rows, not ninety-six. */
  limit?: number;
}
/** The flow's answer stamped with the FRAME it was asked in (`lib/bands`): the rows and the bands are read in it,
 *  never in the URL's, which may already say otherwise while the next answer comes. */
export type MediaAnswer = MediaResponse & { frame: ListFrame<MediaGrain> };

/** The flow — ONE page of `FLOW_PAGE_SIZE` under the pager (the list-read kit's page, ADR-078 §4: the band stream
 *  with every band open); grouped, the server's dividers — every band of the filtered library with its TRUE count —
 *  ride the answer, each row stamped with its band. The flow's HEAD is the live page (`liveGet` on page 1 alone — a
 *  look-again re-reads that one page; a deeper page is a plain read, as the hub's); the previous answer stays while
 *  the next comes (its own frame with it). */
export function mediaQueryOptions(projectId: string, q: FilesQuestion, view: MediaView) {
  const offset = (view.page - 1) * FLOW_PAGE_SIZE;
  const limit = view.limit ?? FLOW_PAGE_SIZE;
  const params = { ...clean(q), ...(view.review ? { review: 'true' } : {}), sort: view.sort, limit, offset, ...(view.group ? { group_by: view.group, ...(view.tz ? { tz: view.tz } : {}) } : {}) };
  return queryOptions({
    queryKey: filesKeys.media(projectId, q, view.review, view.sort, view.group ?? '', view.group ? (view.tz ?? '') : '', view.page, limit),
    queryFn: (ctx): Promise<MediaAnswer> =>
      (offset === 0
        ? liveGet<MediaResponse>(ctx, `/projects/${projectId}/files/media`, { params })
        : apiClient.get<MediaResponse>(`/projects/${projectId}/files/media`, { params, signal: ctx.signal }).then((r) => r.data)
      ).then(framed<MediaGrain>({ group_by: view.group ?? undefined, offset })),
  });
}
export function useMedia(projectId: string, q: FilesQuestion, view: MediaView): UseQueryResult<MediaAnswer, Error> {
  return useQuery({ ...mediaQueryOptions(projectId, q, view), enabled: projectId.length > 0, placeholderData: (prev) => prev });
}

/** The flow's COUNT alone on the same predicates (ADR-078 §6) — the phone filter
 *  drawer's "Show results (n)", a background question (`quietBusy`). */
export async function countMedia(projectId: string, q: FilesQuestion, opts: { review: boolean }, signal?: AbortSignal): Promise<number> {
  const r = await apiClient.get<CountResponse>(`/projects/${projectId}/files/media/count`, { params: { ...clean(q), ...(opts.review ? { review: 'true' } : {}) }, signal, quietBusy: true });
  return r.data.total;
}

/** The shelf once, for a count — the anchor's documents on the question (whole: documents are few by construction), a background question. */
async function readShelfOnce(projectId: string, q: FilesQuestion, signal?: AbortSignal): Promise<ShelfResponse> {
  const r = await apiClient.get<ShelfResponse>(`/projects/${projectId}/files/shelf`, { params: clean(q), signal, quietBusy: true });
  return r.data;
}
/** The rows the shelf shows for a question: the anchor's own documents, the lanes' documents, the products' catalog copies. */
const shelfRows = (shelf: ShelfResponse): number => shelf.documents.length + shelf.lanes.reduce((n, lane) => n + lane.items.reduce((m, it) => m + it.documents.length + (it.artifacts?.length ?? 0), 0), 0);

/** The LIBRARY's count on a question — what the surface would show (the phone drawer's "Show results (n)", ADR-080 §13).
 *  The two natures exclude each other by the question's own axes (the library's rule): a Folder or a document File type
 *  shows the shelf alone — its documents are counted on the draft's question; a Topic, a GPS fix or a media File type the
 *  flow alone — its count-only read; otherwise both, added; under the REVIEW lens the flow alone (the library hides the
 *  shelf there). Nothing of the server's filtering is replayed in the browser. */
export async function countFiles(projectId: string, q: FilesQuestion, opts: { review: boolean }, signal?: AbortSignal): Promise<number> {
  const types = (q.type ?? '').split(',').filter(Boolean) as FileTypeFilter[];
  const nature = types.length === 0 ? null : types.every((v) => MEDIA_TYPE_FILTERS.includes(v)) ? 'media' : types.every((v) => !MEDIA_TYPE_FILTERS.includes(v)) ? 'documents' : null;
  const docsOnly = Boolean(q.folder) || nature === 'documents';
  const mediaOnly = Boolean(q.topic) || Boolean(q.position) || nature === 'media';
  if (opts.review) return countMedia(projectId, q, opts, signal);
  if (docsOnly) return shelfRows(await readShelfOnce(projectId, q, signal));
  if (mediaOnly) return countMedia(projectId, q, opts, signal);
  const [media, shelf] = await Promise.all([countMedia(projectId, q, opts, signal), readShelfOnce(projectId, q, signal)]);
  return media + shelfRows(shelf);
}

export function useCollections(projectId: string, q: FilesQuestion, by: 'asset' | 'topic' | null, opts: { review: boolean; sort: 'newest' | 'oldest' }): UseQueryResult<CollectionsResponse, Error> {
  return useQuery({
    queryKey: filesKeys.collections(projectId, q, by ?? '', opts.review, opts.sort),
    queryFn: (ctx) =>
      liveGet<CollectionsResponse>(ctx, `/projects/${projectId}/files/collections`, { params: { ...clean(q), by, ...(opts.review ? { review: 'true' } : {}), sort: opts.sort } }),
    enabled: projectId.length > 0 && by !== null,
    placeholderData: (prev) => prev,
  });
}

/** Every viewer fetches — the server answers per viewer (the librarian's whole
 *  bin; anyone else's own deletions), and the bin DOOR is this read's count. */
export function useBin(projectId: string, anchor: string): UseQueryResult<BinResponse, Error> {
  return useQuery({
    queryKey: filesKeys.bin(projectId, anchor),
    queryFn: (ctx) => liveGet<BinResponse>(ctx, `/projects/${projectId}/files/bin`, { params: { anchor } }),
    enabled: projectId.length > 0 && anchor.length > 0,
  });
}

/** The surface's read — the file + its notes + its older versions. */
export function useFileDetail(projectId: string, fileId: string | null): UseQueryResult<FileDetail, Error> {
  return useQuery({
    queryKey: filesKeys.detail(projectId, fileId ?? ''),
    queryFn: (ctx) => liveGet<FileDetail>(ctx, `/projects/${projectId}/files/${fileId}`),
    enabled: projectId.length > 0 && !!fileId,
  });
}

/** The same read made outside the live client — a form's Load the latest. `quiet`: a file gone meanwhile answers the
 *  form, never the not-found page (ADR-084 §6). */
export async function fetchFile(projectId: string, fileId: string, quiet = false): Promise<FileDetail> {
  return (await apiClient.get<FileDetail>(`/projects/${projectId}/files/${fileId}`, quiet ? { quietNotFound: true } : undefined)).data;
}

// ── the signed links — one batched mint per visible chunk ───────────────────

export interface PreviewWant {
  file_id: string;
  kind: PreviewKind;
}

/** `id:kind` → url. A plain record, not a Map: TanStack's structural sharing keeps a
 *  plain object's identity while its values hold (a background refetch settling with the
 *  same urls hands consumers the same object); a Map would be rebuilt every time. */
export type PreviewLinks = Readonly<Record<string, string | null | undefined>>;
function combineLinks(results: { data?: PreviewLinksResponse }[]): PreviewLinks {
  const links: Record<string, string | null> = {};
  for (const r of results) for (const it of r.data?.items ?? []) links[`${it.file_id}:${it.kind}`] = it.url;
  return links;
}

/** Mints the wanted links in chunks (≤ 100 each), cached just under the TTL,
 *  never refetched on focus. Returns `id:kind` → url (null = not previewable). */
export function usePreviewLinks(projectId: string, wanted: PreviewWant[]): PreviewLinks {
  const sorted = [...wanted].sort((a, b) => `${a.file_id}:${a.kind}`.localeCompare(`${b.file_id}:${b.kind}`));
  const chunks: PreviewWant[][] = [];
  for (let i = 0; i < sorted.length; i += PREVIEW_LINK_BATCH_MAX) chunks.push(sorted.slice(i, i + PREVIEW_LINK_BATCH_MAX));
  // `combine` (a stable function) hands back ONE record that keeps its identity
  // while the urls hold — consumers memo on it without churn.
  const links = useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: filesKeys.previews(projectId, chunk.map((w) => `${w.file_id}:${w.kind}`).join(',')),
      queryFn: () => apiClient.post(`/projects/${projectId}/files/preview-links`, { items: chunk }, MINT).then((r) => r.data as PreviewLinksResponse),
      staleTime: (PREVIEW_LINK_TTL_SECONDS - 10) * 1000,
      gcTime: PREVIEW_LINK_TTL_SECONDS * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    })),
    combine: combineLinks,
  });
  return links;
}

/** One link, on demand (open in a tab). */
export function fetchPreviewLink(projectId: string, fileId: string, kind: PreviewKind): Promise<PreviewLinkResponse> {
  return apiClient.post(`/projects/${projectId}/files/${fileId}/preview-link`, { kind }, MINT).then((r) => r.data as PreviewLinkResponse);
}

// ── the upload — announce → the bytes → the sizes ───────────────────────────

export interface UploadProgress {
  /** 0..1 of the original's bytes sent; `phase: 'sizes'` while the browser makes
   *  and sends the derivative sizes (indeterminate — no byte count to show). */
  fraction: number;
  phase: 'bytes' | 'sizes';
}

/** announce → PUT the bytes (the commit) → PUT the image sizes the browser made.
 *  `signal` aborts the transfer in flight (a closed dialog); the pending row it
 *  leaves behind is the sweeper's. */
export async function uploadFile(projectId: string, announce: AnnounceFileRequest, file: File, onProgress?: (p: UploadProgress) => void, signal?: AbortSignal): Promise<LibraryFile> {
  const announced = await apiClient.post(`/projects/${projectId}/files`, announce, { signal, ...MINT }).then((r) => r.data as AnnouncedFile);
  const committed = await apiClient
    .put(`/projects/${projectId}/files/${announced.id}/content`, file, {
      headers: OCTET,
      ...NO_TIMEOUT,
      signal,
      onUploadProgress: (e) => onProgress?.({ fraction: e.total ? e.loaded / e.total : 0, phase: 'bytes' }),
    })
    .then((r) => r.data as LibraryFile);
  if (announced.derivative_kinds.length && committed.family === 'image') {
    onProgress?.({ fraction: 1, phase: 'sizes' });
    const sizes = await makeDerivatives(file, announced.derivative_kinds);
    for (const kind of announced.derivative_kinds) {
      const blob = sizes[kind];
      if (!blob) continue;
      // A missing thumb is not a failed upload — the original is committed.
      await apiClient.put(`/projects/${projectId}/files/${announced.id}/derivatives/${kind}`, blob, { headers: OCTET, ...NO_TIMEOUT, signal }).catch(() => undefined);
    }
  }
  return committed;
}

// ── the verbs ───────────────────────────────────────────────────────────────

function useFilesInvalidate(projectId: string): () => Promise<void> {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: filesKeys.all(projectId) });
}

export function useClassifyFile(projectId: string): UseMutationResult<LibraryFile, Error, { fileId: string } & ClassifyFileRequest> {
  const invalidate = useFilesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ fileId, ...body }) => apiClient.post(`/projects/${projectId}/files/${fileId}/classification`, body).then((r) => r.data as LibraryFile),
    onSettled: refreshOnSettled(invalidate),
  });
}

/** The BULK answer — one request, one audit row with the count (the ticket
 *  peek's "Nothing to classify, close" over every photo still waiting). */
export function useClassifyFiles(projectId: string): UseMutationResult<LibraryFile[], Error, ClassifyFilesRequest> {
  const invalidate = useFilesInvalidate(projectId);
  return useMutation({
    mutationFn: (body) => apiClient.post(`/projects/${projectId}/files/classification`, body).then((r) => (r.data as { items: LibraryFile[] }).items),
    onSettled: refreshOnSettled(invalidate),
  });
}

/** Every file still waiting on an anchor (the review lens, paged to the end by offset — the wire's biggest page) —
 *  the ids a bulk answer takes. */
export async function fetchWaitingFileIds(projectId: string, anchor: string): Promise<string[]> {
  const ids: string[] = [];
  const limit = 100;
  for (let offset = 0; ; offset += limit) {
    const page = await apiClient.get(`/projects/${projectId}/files/media`, { params: { anchor, review: 'true', sort: 'oldest', limit, offset } }).then((r) => r.data as MediaResponse);
    ids.push(...page.items.map((f) => f.id));
    if (page.items.length < limit || offset + limit >= page.total) break;
  }
  return ids;
}

/** The display name (a document's name, a web photo's caption) — the body carries the file's stamp (ADR-084). */
export function useRenameFile(projectId: string): UseMutationResult<LibraryFile, Error, { fileId: string } & UpdateFileRequest> {
  const invalidate = useFilesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ fileId, ...body }) => apiClient.patch(`/projects/${projectId}/files/${fileId}`, body).then((r) => r.data as LibraryFile),
    onSuccess: invalidate,
  });
}

export function useMoveFile(projectId: string): UseMutationResult<LibraryFile, Error, { fileId: string } & MoveFileRequest> {
  const invalidate = useFilesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ fileId, ...body }) => apiClient.post(`/projects/${projectId}/files/${fileId}/move`, body).then((r) => r.data as LibraryFile),
    onSuccess: invalidate,
  });
}

export function useDeleteFile(projectId: string): UseMutationResult<unknown, Error, { fileId: string }> {
  const invalidate = useFilesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ fileId }) => apiClient.delete(`/projects/${projectId}/files/${fileId}`),
    onSettled: refreshOnSettled(invalidate),
  });
}

export function useRestoreFile(projectId: string): UseMutationResult<LibraryFile, Error, { fileId: string }> {
  const invalidate = useFilesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ fileId }) => apiClient.post(`/projects/${projectId}/files/${fileId}/restore`).then((r) => r.data as LibraryFile),
    onSettled: refreshOnSettled(invalidate),
  });
}

export function useAddNote(projectId: string): UseMutationResult<FileNote, Error, { fileId: string; body: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, body }) => apiClient.post(`/projects/${projectId}/files/${fileId}/notes`, { body }).then((r) => r.data as FileNote),
    onSuccess: (_n, { fileId }) => qc.invalidateQueries({ queryKey: filesKeys.detail(projectId, fileId) }),
  });
}

export function useEditNote(projectId: string): UseMutationResult<FileNote, Error, { fileId: string; noteId: string; body: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, noteId, body }) => apiClient.patch(`/projects/${projectId}/files/${fileId}/notes/${noteId}`, { body }).then((r) => r.data as FileNote),
    onSuccess: (_n, { fileId }) => qc.invalidateQueries({ queryKey: filesKeys.detail(projectId, fileId) }),
  });
}

export function useDeleteNote(projectId: string): UseMutationResult<unknown, Error, { fileId: string; noteId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, noteId }) => apiClient.delete(`/projects/${projectId}/files/${fileId}/notes/${noteId}`),
    onSettled: refreshOnSettled((_n, { fileId }) => qc.invalidateQueries({ queryKey: filesKeys.detail(projectId, fileId) })),
  });
}

export function useRestoreNote(projectId: string): UseMutationResult<FileNote, Error, { fileId: string; noteId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, noteId }) => apiClient.post(`/projects/${projectId}/files/${fileId}/notes/${noteId}/restore`).then((r) => r.data as FileNote),
    onSettled: refreshOnSettled((_n, { fileId }) => qc.invalidateQueries({ queryKey: filesKeys.detail(projectId, fileId) })),
  });
}

/** The app-origin download: a copy leaves the system (audited server-side). */
export async function downloadFile(projectId: string, file: Pick<LibraryFile, 'id' | 'original_name' | 'display_name'>): Promise<void> {
  const blob = await apiClient.get(`/projects/${projectId}/files/${file.id}/download`, { responseType: 'blob', ...NO_TIMEOUT }).then((r) => r.data as Blob);
  downloadBlob(blob, file.display_name ?? file.original_name);
}

// ── catalog artifacts — sha-addressed canonical documents (no files rows) ─────
// Served by the files module under the catalog path (ADR-068's fold): the
// download is an attachment with the artifact's title as the filename; the
// preview link rides the files origin like any file's (`k: 'catalog'`).

export function fetchCatalogPreviewLink(projectId: string, sha256: string, name?: string): Promise<PreviewLinkResponse> {
  return apiClient.post(`/projects/${projectId}/catalog/artifacts/${sha256}/preview-link`, name ? { name } : {}, MINT).then((r) => r.data as PreviewLinkResponse);
}

export async function downloadCatalogArtifact(projectId: string, sha256: string, name: string): Promise<void> {
  const blob = await apiClient
    .get(`/projects/${projectId}/catalog/artifacts/${sha256}`, { params: { name }, responseType: 'blob', ...NO_TIMEOUT })
    .then((r) => r.data as Blob);
  downloadBlob(blob, name);
}

// ── the stage's link — one mint per OPEN, fresh at the moment it loads ────────

/** What the stage shows: a library row (the wire file — `derivatives` decide the
 *  web size for a picture) or a catalog synthetic (a `cat:` id, no derivatives). */
export interface StageLinkFile {
  id: string;
  original_name: string;
  previewable: boolean;
  family: ContentFamily;
  derivatives?: readonly { kind: string; ready: boolean }[];
}

/** A link goes dead for a NEW load a little before its TTL: the request must START
 *  inside the TTL, and the client's clock is not the server's — so the expiry is
 *  the client's own receipt time + the TTL, less this margin (never `expires_at`). */
export const STAGE_LINK_MARGIN_MS = 5_000;

interface StageLink {
  url: string;
  expiresAt: number;
  /** Minted by `retry` (the re-mint after dead bytes) — a second death is a refusal. */
  retryOf: boolean;
}

/** The stage's signed link for the OPEN file — minted per open (the query lives
 *  exactly as long as the open: `gcTime` 0), so a reopen, a step away and back,
 *  or a layout that remounts the stage all load a FRESH link, never one that
 *  outlived its TTL in a cache (a frame reports no error for a dead link — the
 *  person would see a blank until a reload). `refresh` mints again when the stage
 *  meets a link past its expiry; `retry` re-mints ONCE after dead bytes, then
 *  `failed` — bytes that keep refusing to render must not mint in a loop. One
 *  hook for every host, library rows and catalog synthetics alike. */
export function useStageLink(projectId: string, file: StageLinkFile | null): { preview: StagePreview; refresh: () => void; retry: () => void } {
  const id = file && file.previewable ? file.id : null;
  const name = file?.original_name;
  // The web size for a picture once it is ready — decided at MINT time inside the
  // query fn, never part of the key: a derivative turning ready mid-open must not
  // re-key the query and swap a loaded link for a second mint.
  const webReady = file?.family === 'image' && (file.derivatives ?? []).some((d) => d.kind === 'web' && d.ready);
  // Why the NEXT mint happens — set by a handler right before its refetch, read at
  // the top of the query fn before any await, so no two mints can clobber it.
  const pending = useRef<'open' | 'expired' | 'retry'>('open');
  // A handler's mint in flight — set synchronously in the handler (two calls in one
  // commit cannot both pass), cleared when the refetch settles.
  const inFlight = useRef(false);
  // The link whose bytes refused after the one retry: the failed card, until a fresh open mints anew.
  const [refused, setRefused] = useState<string | null>(null);
  const { data, isError, refetch } = useQuery({
    queryKey: filesKeys.preview(projectId, id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<StageLink> => {
      const why = pending.current;
      pending.current = 'open';
      const fileId = id!;
      const kind: PreviewKind = webReady ? 'web' : 'original';
      // The client's clock at REQUEST time — the server's TTL runs from its own
      // receipt, later than this, so the client always believes a link dead first.
      const requestedAt = Date.now();
      const link = isCatalogFileId(fileId) ? await fetchCatalogPreviewLink(projectId, catalogShaOf(fileId), name) : await fetchPreviewLink(projectId, fileId, kind);
      return { url: link.url, expiresAt: requestedAt + PREVIEW_LINK_TTL_SECONDS * 1000 - STAGE_LINK_MARGIN_MS, retryOf: why === 'retry' };
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const mint = (why: 'expired' | 'retry') => {
    if (inFlight.current) return;
    inFlight.current = true;
    pending.current = why;
    void refetch().finally(() => {
      inFlight.current = false;
    });
  };
  const preview = useMemo<StagePreview>(
    () => ({
      src: id !== null && data ? data.url : null,
      expiresAt: id !== null && data ? data.expiresAt : null,
      failed: id !== null && (isError || (data !== undefined && refused === data.url)),
    }),
    [id, data, isError, refused],
  );
  return {
    preview,
    refresh: () => {
      if (id !== null) mint('expired');
    },
    retry: () => {
      if (id === null || data === undefined) return;
      if (data.retryOf) {
        setRefused(data.url);
        return;
      }
      mint('retry');
    },
  };
}
